// Avatar preset identity list, extracted from storage.ts so the
// server can validate a persisted selection
// without importing the browser-only localStorage/api helpers that live there.

export type AvatarPresetId =
  | 'fan-1'
  | 'fan-2'
  | 'gaffer-1'
  | 'gaffer-2'
  | 'ultra-1'
  | 'ultra-2'
  | 'pundit-1'
  | 'pundit-2'
  | 'lifer'
  | 'nan'
  // The second set — creatures rather than fan archetypes. Appended after the
  // originals so the picker's existing order is untouched.
  | 'alien'
  | 'bird'
  | 'devil'
  | 'dinosaur'
  | 'eagle'
  | 'elephant'
  | 'giraffe'
  | 'mouse'
  | 'robot'
  | 'tiger'

export const AVATAR_PRESET_IDS: AvatarPresetId[] = [
  'fan-1',
  'fan-2',
  'gaffer-1',
  'gaffer-2',
  'ultra-1',
  'ultra-2',
  'pundit-1',
  'pundit-2',
  'lifer',
  'nan',
  'alien',
  'bird',
  'devil',
  'dinosaur',
  'eagle',
  'elephant',
  'giraffe',
  'mouse',
  'robot',
  'tiger',
]

export function isAvatarPresetId(value: unknown): value is AvatarPresetId {
  return typeof value === 'string' && (AVATAR_PRESET_IDS as string[]).includes(value)
}

// Deterministic texture fallback: when a league member never chose an avatar, the
// detail-screen list still draws a glyph so the row reads as a person, not a blank. The preset is
// derived from the member id so it stays stable run-to-run and never collides with real identity —
// avatars there are texture, and duplicates within a league are expected by design.
export function avatarPresetForId(id: string): AvatarPresetId {
  let hash = 0
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0
  }
  return AVATAR_PRESET_IDS[Math.abs(hash) % AVATAR_PRESET_IDS.length]
}

// Human-readable name per preset, used as the glyph's aria-label. Lives here rather than in
// ProfileAvatar so non-component modules and tests can read it.
export const PRESET_LABEL: Record<AvatarPresetId, string> = {
  'fan-1': 'The fan',
  'fan-2': 'The fan, bob',
  'gaffer-1': 'The gaffer',
  'gaffer-2': 'The gaffer, ponytail',
  'ultra-1': 'The ultra',
  'ultra-2': 'The ultra, long hair',
  'pundit-1': 'The pundit',
  'pundit-2': 'The pundit, long hair',
  lifer: 'The lifer',
  nan: 'The nan',
  alien: 'The alien',
  bird: 'The bird',
  devil: 'The devil',
  dinosaur: 'The dinosaur',
  eagle: 'The eagle',
  elephant: 'The elephant',
  giraffe: 'The giraffe',
  mouse: 'The mouse',
  robot: 'The robot',
  tiger: 'The tiger',
}
